/**
 * Protected Route Component
 * Ensures user is authenticated before accessing routes
 * Saves return destination for post-login redirect
 */

import React from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useSession } from "@/hooks/useSession";
import { Icon3D } from "@/components/ui/icon-3d";
import { sessionManager } from "@/lib/sessionManagement";
import { ROLES } from "@/lib/roles";

interface RequireAuthProps {
  children: JSX.Element;
  requireAdmin?: boolean;
  requirePartner?: boolean;
  redirectTo?: string;
}

export function RequireAuth({
  children,
  requireAdmin = false,
  requirePartner = false,
  redirectTo = "/login"
}: RequireAuthProps) {
  const { user, isLoading } = useSession();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Icon3D name="sync" size="2xl" className="mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - save destination and redirect to login
  if (!user) {
    // Save current location for post-login redirect
    // Exclude demo pages from saved destinations
    if (!location.pathname.includes("/demo")) {
      sessionManager.saveReturnDestination(location.pathname + location.search);
    }

    return <Navigate to={redirectTo} state={{ from: location.pathname }} replace />;
  }

  // Check admin requirement
  if (requireAdmin) {
    const isAdmin =
      user.user_metadata?.role === ROLES.ADMIN ||
      user.user_metadata?.role === ROLES.SUPER_ADMIN ||
      user.app_metadata?.role === ROLES.ADMIN ||
      user.app_metadata?.role === ROLES.SUPER_ADMIN ||
      user.user_metadata?.roles?.includes(ROLES.PLATFORM_ADMIN) ||
      user.app_metadata?.roles?.includes(ROLES.PLATFORM_ADMIN);

    if (!isAdmin) {
      return <Navigate to="/app" replace />;
    }
  }

  // Check partner requirement
  if (requirePartner) {
    const isPartner =
      user.user_metadata?.account_type === ROLES.PARTNER ||
      user.user_metadata?.role === ROLES.PARTNER ||
      user.app_metadata?.role === ROLES.PARTNER ||
      user.user_metadata?.roles?.some((r: string) => r.startsWith('partner_')) ||
      user.app_metadata?.roles?.some((r: string) => r.startsWith('partner_'));

    if (!isPartner) {
      return <Navigate to="/app" replace />;
    }
  }

  // User is authenticated and authorized
  return children;
}

export default RequireAuth;
